import {
  Box,
  BoxProps,
  Button,
  HStack,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
  ModalOverlay,
  Tag,
  useBreakpointValue,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { TransactionSortOption } from "@syla/shared/types/requests/GetGroupsRequest";
import React, { ReactNode, memo } from "react";
import { FiList } from "react-icons/fi";
import { AppModal } from "../../../../modals/AppModal";
import { AppModalContent } from "../../../../modals/AppModalContent";
import { AppModalFooter } from "../../../../modals/AppModalFooter";
import { PageSizeSelector } from "../PageSizeSelector";
import { SortBySelector } from "../SortBySelector";

type ViewSectionProps = {
  sortOption: TransactionSortOption | undefined;
  setSortOption: (value: TransactionSortOption) => void;
  pageSize: number;
  setPageSize: (value: number) => void;
} & BoxProps;

export const ViewSection = memo(function ViewSection({
  sortOption,
  setSortOption,
  pageSize,
  setPageSize,
  ...props
}: ViewSectionProps): React.ReactElement {
  const isMobile = useBreakpointValue({
    base: true,
    sm: false,
  });

  const controls = (
    <ViewControls
      sortOption={sortOption}
      setSortOption={setSortOption}
      pageSize={pageSize}
      setPageSize={setPageSize}
      matchWidth={isMobile}
      isMobile={isMobile}
    />
  );

  if (isMobile) {
    return (
      <Box {...props}>
        <MobileViewLayout sortOption={sortOption} pageSize={pageSize}>
          {controls}
        </MobileViewLayout>
      </Box>
    );
  }

  return (
    <Box {...props}>
      <DesktopViewLayout>{controls}</DesktopViewLayout>
    </Box>
  );
});

function DesktopViewLayout({
  children,
}: {
  children: ReactNode;
}): React.ReactElement {
  return (
    <HStack spacing={4} align="flex-start">
      {children}
    </HStack>
  );
}

function MobileViewLayout({
  children,
  sortOption,
  pageSize,
}: {
  children: ReactNode;
  sortOption: TransactionSortOption | undefined;
  pageSize: number;
}): React.ReactElement {
  const disclosure = useDisclosure();

  return (
    <>
      <Button
        variant="outline"
        onClick={disclosure.onOpen}
        size="sm"
        backgroundColor="white"
        justifyContent="flex-start"
      >
        <HStack spacing={2}>
          <FiList />
          <span>View</span>
          <Tag
            size="sm"
            colorScheme="gray"
            variant="subtle"
            borderRadius="full"
          >
            {getSortLabel(sortOption)}
          </Tag>

          <Tag
            size="sm"
            colorScheme="gray"
            variant="subtle"
            borderRadius="full"
          >
            {pageSize}
          </Tag>
        </HStack>
      </Button>

      <AppModal
        isOpen={disclosure.isOpen}
        onClose={disclosure.onClose}
        size="full"
      >
        <ModalOverlay />

        <AppModalContent>
          <ModalHeader>View options</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack align="stretch" spacing={4}>
              {children}
            </VStack>
          </ModalBody>

          <AppModalFooter>
            <Button colorScheme="primary" onClick={disclosure.onClose}>
              Done
            </Button>
          </AppModalFooter>
        </AppModalContent>
      </AppModal>
    </>
  );
}

function ViewControls({
  sortOption,
  setSortOption,
  pageSize,
  setPageSize,
  matchWidth,
  isMobile,
}: ViewSectionProps & {
  matchWidth?: boolean;
  isMobile?: boolean;
}): React.ReactElement {
  return (
    <>
      <SortBySelector
        size={isMobile ? "md" : "sm"}
        value={sortOption ?? TransactionSortOption.DateAscending}
        onChange={setSortOption}
        buttonProps={{
          flexShrink: 0,
          backgroundColor: "white",
        }}
        popoverProps={{ matchWidth }}
      />

      <PageSizeSelector
        size={isMobile ? "md" : "sm"}
        value={pageSize}
        onChange={setPageSize}
        buttonProps={{
          flexShrink: 0,
          backgroundColor: "white",
        }}
        popoverProps={{ matchWidth }}
      />
    </>
  );
}

function getSortLabel(sortOption: TransactionSortOption | undefined): string {
  switch (sortOption ?? TransactionSortOption.DateAscending) {
    case TransactionSortOption.DateAscending:
      return "Oldest first";

    case TransactionSortOption.DateDescending:
      return "Newest first";

    default:
      return "Sort";
  }
}
